import type { ReactNode } from 'react';

import { Flex } from '@kognitos/lattice';

import { AutomationDocumentTitleBar } from '@/modules/automation/components/AutomationDocumentTitleBar';

interface IBlueprintDocumentViewProps {
  /** The automation this document belongs to — renamed and acted on from the title bar. */
  automationId?: string;
  /** The automation's display name, shown in the title bar. */
  title?: string;
  /** ISO timestamp of the last blueprint save, joined into the stage's meta line. */
  lastSavedAt?: string;
  /** Whether the in-document name can be renamed. */
  isTitleEditable?: boolean;
  /** Current version. Named in the version-history tooltip only. */
  version?: string;
  /** Opens version history from the title bar's right cluster. */
  onOpenVersionHistory?: () => void;
  /** Export the automation, offered in the title bar's options menu. */
  onExport?: () => void;
  /**
   * Replaces the stage's "Not built yet" meta while the build is running, has
   * ended without code, or quill's staged edits are waiting on review.
   */
  status?: ReactNode;
  /**
   * The blueprint's own CTAs — Approve & build, Build again, or the staged
   * review's Keep / Discard pair — placed in the title bar's right cluster.
   */
  actions?: ReactNode;
  /** The document body: the markdown editor, its staged-review diff, or a skeleton. */
  children: ReactNode;
}

/**
 * The blueprint's document shell: the same card, sticky title bar and centred
 * column a ready draft gets from `DraftDocumentStepsView`, wrapped around the
 * markdown blueprint instead of a step list.
 *
 * Only the frame is shared. Everything that makes this the blueprint — the
 * dashed edge and build ring outside the card, the editable preview inside it,
 * and the CTAs in `actions` — still comes from the blueprint phase itself.
 */
export function BlueprintDocumentView({
  automationId,
  title,
  lastSavedAt,
  isTitleEditable = false,
  version,
  onOpenVersionHistory,
  onExport,
  status,
  actions,
  children,
}: IBlueprintDocumentViewProps) {
  return (
    /* Spec 3, as on the draft: the card is the frame and its bottom edge stays
     * pinned to the viewport — the scroll lives *inside* it. The border is the
     * one difference: a blueprint is outlined by the dashed frame its phase
     * draws around this card, so a solid one here would double it. */
    <Flex
      direction="column"
      className="h-full w-full shrink-0 overflow-hidden rounded-2xl bg-card shadow-xs"
      data-testid="blueprint-document-view"
    >
      <Flex
        direction="column"
        className="min-h-0 w-full flex-1 overflow-y-auto px-10 pb-10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="blueprint-document-scroll"
      >
        <Flex direction="column" gap={6} width="full">
          {/* Spec 1: the title bar is the scroll container's first child, so it
              stays pinned to the card's top edge while the document scrolls
              under it. */}
          <AutomationDocumentTitleBar
            stage="blueprint"
            automationId={automationId}
            title={title}
            lastSavedAt={lastSavedAt}
            isTitleEditable={isTitleEditable}
            version={version}
            onOpenVersionHistory={onOpenVersionHistory}
            onExport={onExport}
            status={status}
            actions={actions}
          />
          {/* Content is sized by what it holds, never stretched to the
              scroller: a flex-1 column here would let a long blueprint be
              squeezed to fit instead of scrolling. */}
          <Flex direction="column" className="mx-auto w-full max-w-[720px]">
            {children}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
